<template>
  <b-container>
    <b-modal
      ref="addDistrictModal"
      id="addDistrictModal"
      hide-footer
      title="Add New School District"
    >
      <b-form @submit="saveDistrict">
        <b-row class="my-2">
          <b-col sm="3">
            <label for="districtState">
              District State:
              <span class="required"></span>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-select
              id="state"
              v-model="districtState"
              :options="states"
              value-field="stateCode"
              text-field="stateName"
              required
            ></b-form-select>
          </b-col>
        </b-row>

        <b-row class="my-1">
          <b-col sm="3">
            <label for="districtName">District Name:</label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="object"
              v-model="districtName"
              type="text"
            ></b-form-input>
          </b-col>
        </b-row>

        <b-button variant="outline-primary" type="submit">
          <b-icon-archive-fill></b-icon-archive-fill>Save
        </b-button>
        <b-button
          variant="outline-primary"
          @click.prevent="onCancel"
          type="cancel"
        >
          <b-icon-archive-fill></b-icon-archive-fill>Cancel
        </b-button>
      </b-form>
    </b-modal>

    <h1>Student Information</h1>
    <div class="jumbotron">
      <b-form @submit.prevent="putStudent" v-show="!error">
        <b-row class="my-2">
          <b-col sm="3">
            <label for="fname">
              First Name:
              <span class="required"></span>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="fname"
              size="64"
              v-model="bioDemo.firstName"
              :state="validateState('firstName')"
              :class="{
                'is-invalid': $v.bioDemo.firstName.$error,
                'is-valid': !$v.bioDemo.firstName.$invalid,
              }"
              type="text"
              @input="$v.bioDemo.firstName.$touch"
              aria-describedby="first-name"
              required
            ></b-form-input>
            <div class="invalid-feedback">
              <span v-if="!$v.bioDemo.firstName.required"
                >Please fill in your First Name</span
              >
              <span v-if="!$v.bioDemo.firstName.minLength"
                >First Name needs a minimum length of
                {{ $v.bioDemo.firstName.$params.minLength.min }}</span
              >
              <span v-if="!$v.bioDemo.firstName.maxLength"
                >First Name must have at most
                {{ $v.bioDemo.firstName.$params.maxLength.max }}
                characters</span
              >
            </div>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="lname">
              Last Name:
              <span class="required"></span>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="lname"
              size="64"
              v-model="bioDemo.lastName"
              :state="validateState('lastName')"
              :class="{
                'is-invalid': $v.bioDemo.lastName.$error,
                'is-valid': !$v.bioDemo.lastName.$invalid,
              }"
              type="text"
              @input="$v.bioDemo.lastName.$touch"
              aria-describedby="last-name"
              required
            ></b-form-input>
            <div class="invalid-feedback">
              <span v-if="!$v.bioDemo.lastName.required"
                >Please fill in your Last Name</span
              >
              <span v-if="!$v.bioDemo.lastName.minLength"
                >Last Name needs a minimum length of
                {{ $v.bioDemo.lastName.$params.minLength.min }}</span
              >
              <span v-if="!$v.bioDemo.lastName.maxLength"
                >Last Name must have at most
                {{ $v.bioDemo.lastName.$params.maxLength.max }}
                characters</span
              >
            </div>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="pname">Previous/Maiden Name:</label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="pname"
              maxlength="64"
              size="64"
              v-model="bioDemo.previousName"
              type="text"
            ></b-form-input>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="street1">
              Street Line 1:
              <span class="required"></span>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="street1"
              maxlength="256"
              size="256"
              v-model="bioDemo.streetLineOne"
              :state="validateState('streetLineOne')"
              :class="{
                'is-invalid': $v.bioDemo.streetLineOne.$error,
                'is-valid': !$v.bioDemo.streetLineOne.$invalid,
              }"
              type="text"
              @input="$v.bioDemo.streetLineOne.$touch"
              aria-describedby="address-line-1"
              required
            ></b-form-input>
            <div class="invalid-feedback">
              <span v-if="!$v.bioDemo.streetLineOne.required"
                >Please fill in your Street Address</span
              >
              <span v-if="!$v.bioDemo.streetLineOne.minLength"
                >Street Address needs a minimum length of
                {{ $v.bioDemo.streetLineOne.$params.minLength.min }}</span
              >
              <span v-if="!$v.bioDemo.streetLineOne.maxLength"
                >Street Address must have at most
                {{ $v.bioDemo.streetLineOne.$params.maxLength.max }}
                characters</span
              >
            </div>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="street2">Street Line 2:</label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="street2"
              maxlength="256"
              size="256"
              v-model="bioDemo.streetLineTwo"
              type="text"
            ></b-form-input>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="city">
              City:
              <span class="required"></span>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="city"
              maxlength="64"
              size="64"
              v-model="bioDemo.city"
              :state="validateState('city')"
              :class="{
                'is-invalid': $v.bioDemo.city.$error,
                'is-valid': !$v.bioDemo.city.$invalid,
              }"
              type="text"
              @input="$v.bioDemo.city.$touch"
              aria-describedby="city"
              required
            ></b-form-input>
            <div class="invalid-feedback">
              <span v-if="!$v.bioDemo.city.required"
                >Please fill in your City</span
              >
              <span v-if="!$v.bioDemo.city.minLength"
                >City needs a minimum length of
                {{ $v.bioDemo.city.$params.minLength.min }}</span
              >
              <span v-if="!$v.bioDemo.city.maxLength"
                >City must have at most
                {{ $v.bioDemo.city.$params.maxLength.max }}
                characters</span
              >
            </div>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="state">
              State:
              <span class="required"></span>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-select
              id="state"
              v-model="bioDemo.state"
              :options="states"
              value-field="stateCode"
              text-field="stateName"
              :class="{
                'is-invalid': $v.bioDemo.state.$error,
                'is-valid': !$v.bioDemo.state.$invalid,
              }"
              :state="validateState('state')"
              @input="$v.bioDemo.state.$touch"
            >
              <div class="invalid-feedback">
                <span v-if="!$v.bioDemo.state.required"
                  >Please select your State</span
                >
              </div>
            </b-form-select>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="zip">
              ZIP:
              <span class="required"></span>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="zip"
              size="5"
              v-model="bioDemo.zip"
              type="number"
              :class="{
                'is-invalid': $v.bioDemo.zip.$error,
                'is-valid': !$v.bioDemo.zip.$invalid,
              }"
              :state="validateState('zip')"
              @input="$v.bioDemo.zip.$touch"
            ></b-form-input>
            <div class="invalid-feedback">
              <span v-if="!$v.bioDemo.zip.required"
                >Please fill in your Zip Code</span
              >
              <span v-if="!$v.bioDemo.zip.minLength"
                >Zip needs a minimum length of
                {{ $v.bioDemo.zip.$params.minLength.min }}</span
              >
              <span v-if="!$v.bioDemo.zip.maxLength"
                >Zip must have at most
                {{ $v.bioDemo.zip.$params.maxLength.max }}
                characters</span
              >
            </div>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="phone">
              Phone Number:
              <span class="required"></span>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="phone"
              size="10"
              v-model="bioDemo.phoneNumber"
              placeholder="(123)456-7890"
              :class="{
                'is-invalid': $v.bioDemo.phoneNumber.$error,
                'is-valid': !$v.bioDemo.phoneNumber.$invalid,
              }"
              :state="validateState('phoneNumber')"
              type="text"
              @input="$v.bioDemo.phoneNumber.$touch"
              @blur="formatPhone"
            ></b-form-input>
            <div class="invalid-feedback">
              <span v-if="!$v.bioDemo.phoneNumber.required"
                >Please fill in your Phone Number</span
              >
            </div>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="email">
              Email:
              <span class="required"></span>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="email"
              maxlength="256"
              size="256"
              v-model="bioDemo.email"
              :class="{
                'is-invalid': $v.bioDemo.email.$error,
                'is-valid': !$v.bioDemo.email.$invalid,
              }"
              :state="validateState('email')"
              type="text"
              @input="$v.bioDemo.email.$touch"
            ></b-form-input>
            <div class="invalid-feedback">
              <span v-if="!$v.bioDemo.email.required"
                >Please fill in your Email Address</span
              >
            </div>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="6" md="3">
            <label for="bdate">
              Date of Birth: <span class="required"></span>
            </label>
          </b-col>

          <b-col md="9" class="d-block d-sm-block d-md-block d-lg-none">
            <birth-datepicker
              v-model="bioDemo.birthDate"
              :valueIsString="true"
              id="birthDate"
              :yearFirst="true"
              type="text"
              :delimiter="'-'"
              :maxYear="2000"
              :minYear="1930"
              :inline="true"
              high="1"
              :class="{
                'is-invalid': $v.bioDemo.birthDate.$error,
                'is-valid': !$v.bioDemo.birthDate.$invalid,
              }"
              :state="validateState('birthDate')"
              @input="$v.bioDemo.birthDate.$touch"
              required
            />
          </b-col>
          <b-col md="9" class="d-none d-sm-none d-md-none d-lg-block">
            <birth-datepicker
              v-model="bioDemo.birthDate"
              :valueIsString="true"
              id="birthDate"
              :yearFirst="true"
              type="text"
              :delimiter="'-'"
              :maxYear="2000"
              :minYear="1930"
              :inline="true"
              :class="{
                'is-invalid': $v.bioDemo.birthDate.$error,
                'is-valid': !$v.bioDemo.birthDate.$invalid,
              }"
              :state="validateState('birthDate')"
              @input="$v.bioDemo.birthDate.$touch"
              required
            />
          </b-col>
        </b-row>
        <b-form-group label="Gender:">
          <b-form-radio v-model="bioDemo.gender" name="gender" value="M"
            >Male</b-form-radio
          >
          <b-form-radio v-model="bioDemo.gender" name="gender" value="F"
            >Female</b-form-radio
          >
          <b-form-radio v-model="bioDemo.gender" name="gender" value="O"
            >Other/Prefer not to disclose</b-form-radio
          >
        </b-form-group>

        <b-form-group label="Ethnicity:">
          <b-form-radio v-model="bioDemo.hispanic" name="ethnicity" value="true"
            >Hispanic</b-form-radio
          >
          <b-form-radio
            v-model="bioDemo.hispanic"
            name="ethnicity"
            value="false"
            >Not Hispanic</b-form-radio
          >
        </b-form-group>

        <b-form-group label="Race (select all that apply):">
          <b-form-checkbox-group
            id="checkbox-group-race"
            v-model="bioDemo.races"
            name="race"
          >
            <b-form-checkbox value="nativeAmerican"
              >American Indian or Alaska Native</b-form-checkbox
            >
            <b-form-checkbox value="asian">Asian</b-form-checkbox>
            <b-form-checkbox value="black"
              >Black or African American</b-form-checkbox
            >
            <b-form-checkbox value="pacificIslander"
              >Native Hawaiian or Other Pacific Islander</b-form-checkbox
            >
            <b-form-checkbox value="white">White</b-form-checkbox>
          </b-form-checkbox-group>
        </b-form-group>

        <b-form-group label="Do you have a college degree?">
          <b-form-radio
            v-model="bioDemo.collegeDegree"
            name="degree"
            value="true"
            >Yes</b-form-radio
          >
          <b-form-radio
            v-model="bioDemo.collegeDegree"
            name="degree"
            value="false"
            >No</b-form-radio
          >
        </b-form-group>

        <b-form-group label="Are you registered for a current or future term?">
          <b-form-radio
            v-model="bioDemo.registered"
            name="registered"
            value="true"
            >Yes</b-form-radio
          >
          <b-form-radio
            v-model="bioDemo.registered"
            name="registered"
            value="false"
            >No</b-form-radio
          >
        </b-form-group>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="westernId">Western ID (if known):</label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="westernId"
              v-model="bioDemo.westernId"
              type="text"
              size="6"
              maxlength="6"
              minlength="6"
            >
            </b-form-input>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="district">
              School District:
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-select
              id="district"
              v-model="selectedDistrict"
              :options="districts"
              value-field="districtId"
              text-field="label"
            >
            </b-form-select>
          </b-col>
        </b-row>

        <b-row class="my-2">
          <b-col sm="3">
            <label for="acceptCancellation"> Cancellation Policy:</label>
          </b-col>
          <b-col sm="9">
            <b-form-checkbox
              id="acceptCancellation"
              v-model="bioDemo.acceptCancellation"
            >
              By Checking this box I acknowledge I have read and understand the
              <b-link
                style="text-decoration: underline !important;"
                v-b-modal.modal-1
                >cancellation policy</b-link
              >

              <b-modal id="modal-1" title="COURSE CANCELLATION POLICY">
                <p
                  class="my-4"
                  v-html="cancellationPolicyText[0].cancellationPolicyText"
                ></p> </b-modal
              >.
            </b-form-checkbox>
          </b-col></b-row
        >

        <!--  <b-row class="my-2">
          <b-col sm="3">
            <label for="howFoundUs"
              >List one participant who referred you:
              <span class="required"></span
            ></label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="howFoundUs"
              v-model="bioDemo.howFoundUs"
              type="text"
              aria-describedby="howFoundUs"
            >
            </b-form-input>
          </b-col>
        </b-row> -->

        <b-row class="my-2" v-show="false">
          <b-col sm="3">
            <label for="alergy">List Alergies?</label>
          </b-col>
          <b-col sm="9">
            <b-form-input
              id="alergy"
              maxlength="512"
              size="512"
              v-model="bioDemo.alergy"
              type="text"
            ></b-form-input>
          </b-col>
        </b-row>

        <b-button
          variant="danger"
          class="d-block mt-2"
          size="block"
          type="submit"
          :disabled="bioDemo.acceptCancellation == false"
          >Review</b-button
        >
      </b-form>
    </div>
  </b-container>
</template>

<script>
//import birthDatepicker from "vue-birth-datepicker";
import birthDatepicker from "vue-birth-datepicker/src/birth-datepicker";
// if substitution is working:
//import birthDatepicker from "vue-birth-datepicker/vue";
import { mixins } from "../mixins";
import { validationMixin } from "vuelidate";
import {
  required,
  minLength,
  maxLength,
  email,
} from "vuelidate/lib/validators";

export default {
  name: "biodemo",
  mixins: [mixins, validationMixin],
  components: {
    birthDatepicker,
  },
  validations: {
    bioDemo: {
      firstName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(60),
      },
      lastName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(60),
      },
      streetLineOne: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(30),
      },
      city: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(30),
      },
      state: {
        required,
      },
      zip: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(5),
      },
      phoneNumber: {
        required,
      },
      email: {
        required,
        email,
      },
      birthDate: {
        required,
      },
    },
  },
  data() {
    return {
      bioDemo: {
        races: [],
        howFoundUs: "",
      },
      states: [],
      districts: [],
      selectedDistrict: null,
      value: "",
      error: false,
      districtState: null,
      districtName: null,
      districtId: "",
      firstName: "",
      lastName: "",
      streetLineOne: "",
      city: "",
      state: "",
      zip: "",
      phoneNumber: "",
      email: "",
      birthDate: "",
      acceptCancellation: false,
      cancellationPolicyText: [],
    };
  },
  created() {
    this.bioDemo.cartId = this.$store.getters.cartId;
    if (this.bioDemo.cartId == 0) {
      this.errorToast(
        "You need to add items to your cart before entering Bio/Demo information."
      );
      this.$router.push({
        path: "/",
      });
      this.error = true;
    } else {
      this.getStudent();
      this.getCancellationPolicy();
    }
  },
  methods: {
    onCancel() {
      this.$bvModal.hide("addDistrictModal");
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.bioDemo[name];
      return $dirty ? !$error : null;
    },
    getStudent() {
      this.api
        .post("/getStudent", {
          cartId: this.bioDemo.cartId,
        })
        .then((response) => {
          if (response.data) {
            this.bioDemo = response.data.student;
            if (this.bioDemo.state == undefined) {
              this.bioDemo.state = "CO";
            }
            this.states = response.data.states;
            this.selectedDistrict = response.data.student.districtId;
            this.districts = response.data.districts;
            this.districts.unshift({
              districtId: 0,
              label: "OTHER/Not listed",
            });
          }
        })
        .catch((error) => {
          if (error.response) {
            this.errorToast(error.response.data.message);
          } else {
            this.errorToast(error.message || "");
          }
        });
    },
    getCancellationPolicy() {
      this.api
        .get("/cancellationPolicyText")
        .then((res) => {
          this.cancellationPolicyText = res.data;
        })
        .catch((error) => {
          if (error.response.status === 401) {
            this.cancellationPolicyText = {
              title: "ERROR:",
            };
          } else {
            this.cancellationPolicyText = {
              title: "ERROR:",
            };
          }
        });
    },
    putStudent(evt) {
      evt.preventDefault();
      this.$v.bioDemo.$touch();
      if (this.$v.bioDemo.$anyError) {
        alert("Please review your entries, this form has errors");
        return;
      }
      this.api
        .post("/putStudent", this.bioDemo)
        .then(() => {
          this.$store.commit("setBioDemoSubmitted", true);
          this.$router.push({
            path: "/complete",
          });
        })
        .catch((error) => {
          this.errorToast(error.response.data.message || error.message || "");
        });
    },
    saveDistrict(evt) {
      evt.preventDefault();
      this.api
        .post("/addDistrict", {
          districtState: this.districtState,
          districtName: this.districtName,
        })
        .then((response) => {
          this.districts = response.data;
          this.districtState = null;
          this.districtName = null;
          this.successToast("Your School District has been added to the list.");
          this.$bvModal.hide("addDistrictModal");
        })
        .catch((error) => {
          this.errorToast(error.response.data.message || error.message || "");
        });
    },
    formatPhone: function() {
      //alert("blur");
      var formattedPhone = this.bioDemo.phoneNumber.replace(/[\s()-]+/gi, "");
      //this.student.phone_number = formattedPhone;
      this.bioDemo.phoneNumber = formattedPhone;
      return formattedPhone;
    },
    /* focusIn: function() {

    }, */
  },
  watch: {
    selectedDistrict: function(district) {
      if (district == 0) {
        this.$bvModal.show("addDistrictModal");
      } else {
        this.bioDemo.districtId = district;
      }
    },
    westernIdWatch: function(westernId) {
      if (westernId !== undefined && westernId !== null) {
        if (westernId.length > 0) {
          westernId = westernId.replace(/\D/g, "");
          if (westernId.length !== 6) {
            alert("Invalid Western ID");
          }
        }
      }
    },
  },
};
</script>

<style>
.birthday-picker a {
  color: #000 !important;
}
.birthday-picker a:hover {
  color: #000 !important;
}
.birthday-picker_dropdown-header {
  background: #a71930 !important;
}
.birthday-picker td a.bdp-active {
  background-color: #a71930 !important;
  color: #fff !important;
}
.birthday-picker td a.bdp-active:hover {
  background-color: #911930 !important;
}
#birthDate.is-valid {
  border-color: #28a745;
  padding-right: calc(1.5em + 0.75rem);
  /* background-image: url(data:image/svg+xml,%3csvg xmlns=http://www.w3.org/2000/svg width=8 height=8 viewBox=0 0 8 8%3e%3cpath fill=%2328a745 d=M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z/%3e%3c/svg%3e); */
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}
</style>
